import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TelemetryResourceOverviewDTO } from '@webip/shared/src/dto/telemetry';
import {
    telemetryStatusMeta,
    formatPercent,
    formatBytes,
    formatUsage,
    formatUptime,
    formatCollectedAt,
} from '@/lib/telemetryFormat';

/**
 * Modal de telemetria compartilhada por appliances e dispositivos de LAN
 * (host-agents). Mostra os cards de estado atual — CPU, Memória e Disco são
 * selecionáveis e alimentam o gráfico — e seletor de período (24h/7d/30d).
 *
 * O endpoint do overview varia por tipo de recurso; quem usa o componente
 * monta a URL e recebe o período escolhido via `overviewUrl(period)`.
 */

export type TelemetryMetric = 'cpu' | 'memory' | 'disk';

export type TelemetryPeriod = '24h' | '7d' | '30d';

const METRIC_META: Record<TelemetryMetric, { label: string }> = {
    cpu: { label: 'CPU' },
    memory: { label: 'Memória' },
    disk: { label: 'Disco' },
};

const PERIOD_META: Record<TelemetryPeriod, { label: string; chartLabel: string }> = {
    '24h': { label: '24h', chartLabel: 'nas últimas 24 horas' },
    '7d': { label: '7d', chartLabel: 'nos últimos 7 dias' },
    '30d': { label: '30d', chartLabel: 'nos últimos 30 dias' },
};

type Props = {
    show: boolean;
    title: React.ReactNode;
    onClose: () => void;
    /** URL do endpoint de overview; recebe o período escolhido. */
    overviewUrl: (period: TelemetryPeriod) => string;
    errorMessage: string;
    /** Conteúdo opcional acima dos cards (ex.: ícone de SO do dispositivo). */
    children?: React.ReactNode;
};

const TelemetryOverviewModal = ({ show, title, onClose, overviewUrl, errorMessage, children }: Props) => {
    const [metric, setMetric] = React.useState<TelemetryMetric>('cpu');
    const [period, setPeriod] = React.useState<TelemetryPeriod>('24h');

    // A URL só existe com o modal aberto — o callback nos call sites assume o
    // recurso não-nulo (`telemetryDevice!.id`), e o componente monta mesmo com
    // show=false (o Modal controla a visibilidade por dentro).
    const overviewPath = show ? overviewUrl(period) : null;

    const queryOverview = useQuery<TelemetryResourceOverviewDTO>({
        queryKey: ['telemetry-overview-modal', overviewPath],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: TelemetryResourceOverviewDTO }>(overviewUrl(period), {
                withCredentials: true,
            });
            return res.data.result;
        },
        enabled: show && overviewPath != null,
        refetchInterval: 60_000,
    });

    // Série em % pra métrica selecionada. Memória/disco só têm bytes usados na
    // série — o total vem do snapshot atual (total de memória/disco muda
    // raramente; recalcular por ponto com ele é suficiente pro gráfico).
    const chartData = React.useMemo(() => {
        const current = queryOverview.data?.current;
        // A série vem do backend com o mais recente primeiro — o gráfico lê
        // da esquerda pra direita, então ordena ascendente antes de mapear.
        const series = [...(queryOverview.data?.series ?? [])].sort(
            (a, b) => new Date(a.collected_at).getTime() - new Date(b.collected_at).getTime(),
        );
        return series.map((point) => {
            const label = period === '24h'
                ? new Date(point.collected_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                : new Date(point.collected_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

            if (metric === 'cpu') {
                return {
                    label,
                    avg: point.cpu_percent_avg == null ? null : Math.round(point.cpu_percent_avg),
                    max: point.cpu_percent_max == null ? null : Math.round(point.cpu_percent_max),
                };
            }

            const total = metric === 'memory' ? current?.memory_total_bytes : current?.disk_total_bytes;
            const toPercent = (used?: number | null) =>
                used == null || total == null || total <= 0 ? null : Math.round((used / total) * 100);
            const usedAvg = metric === 'memory' ? point.memory_used_bytes_avg : point.disk_used_bytes_avg;
            const usedMax = metric === 'memory' ? point.memory_used_bytes_max : point.disk_used_bytes_max;
            return { label, avg: toPercent(usedAvg), max: toPercent(usedMax) };
        });
    }, [queryOverview.data, metric, period]);

    // Média/pico do período, na métrica selecionada. Memória/disco vêm em bytes
    // — mostramos % contra o total atual; sem total conhecido, cai pro valor bruto.
    const statValue = (kind: 'avg' | 'max') => {
        const stats = queryOverview.data?.stats;
        if (!stats) return 'n/d';
        if (metric === 'cpu') {
            return formatPercent(kind === 'avg' ? stats.cpu_percent_avg : stats.cpu_percent_max);
        }
        const total = metric === 'memory'
            ? queryOverview.data?.current?.memory_total_bytes
            : queryOverview.data?.current?.disk_total_bytes;
        const raw = metric === 'memory'
            ? (kind === 'avg' ? stats.memory_used_bytes_avg : stats.memory_used_bytes_max)
            : (kind === 'avg' ? stats.disk_used_bytes_avg : stats.disk_used_bytes_max);
        if (raw == null) return 'n/d';
        if (total != null && total > 0) return `${Math.round((raw / total) * 100)}%`;
        return formatBytes(raw);
    };

    return (
        <Modal show={show} title={title} onClose={onClose} onCancel={onClose} hideControls size="lg">
            <VBox style={{ gap: '16px' }}>
                {children}

                {queryOverview.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                        <Icon path={mdiLoading} spin size={1.5} />
                    </HBox>
                )}

                {queryOverview.isError && (
                    <p>{errorMessage}</p>
                )}

                {!queryOverview.isLoading && !queryOverview.isError && queryOverview.data && (
                    <>
                        <HBox style={{ gap: '12px', flexWrap: 'wrap' }}>
                            {([
                                {
                                    metric: 'cpu' as TelemetryMetric,
                                    label: 'CPU atual',
                                    value: formatPercent(queryOverview.data.current?.cpu_percent),
                                },
                                {
                                    metric: 'memory' as TelemetryMetric,
                                    label: 'Memória atual',
                                    value: formatUsage(
                                        queryOverview.data.current?.memory_used_bytes,
                                        queryOverview.data.current?.memory_total_bytes,
                                    ),
                                },
                                {
                                    metric: 'disk' as TelemetryMetric,
                                    label: 'Disco atual',
                                    value: formatUsage(
                                        queryOverview.data.current?.disk_used_bytes,
                                        queryOverview.data.current?.disk_total_bytes,
                                    ),
                                },
                                {
                                    metric: null,
                                    label: 'Uptime',
                                    value: formatUptime(queryOverview.data.current?.uptime_seconds),
                                },
                            ]).map((item) => {
                                const selectable = item.metric != null;
                                const selected = selectable && item.metric === metric;
                                // div nativo em vez de VBox: o VBox não repassa
                                // onClick/role — cliques nos cards eram engolidos.
                                return (
                                    <div
                                        key={item.label}
                                        onClick={selectable ? () => setMetric(item.metric!) : undefined}
                                        role={selectable ? 'button' : undefined}
                                        aria-pressed={selectable ? selected : undefined}
                                        style={{
                                            flex: '1 1 220px',
                                            border: selected ? '2px solid var(--tc-color-primary)' : '1px solid var(--border-1)',
                                            borderRadius: '12px',
                                            padding: selected ? '11px 13px' : '12px 14px',
                                            gap: '6px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: selectable ? 'pointer' : 'default',
                                            background: selected ? 'var(--telemetry-card-selected-bg)' : undefined,
                                        }}
                                    >
                                        <span style={{ fontSize: '12px', color: selected ? 'var(--tc-color-primary)' : 'var(--color-muted)', fontWeight: 600 }}>
                                            {item.label}
                                        </span>
                                        <span style={{ fontSize: '18px', color: 'var(--color-body)', fontWeight: 700 }}>{item.value}</span>
                                    </div>
                                );
                            })}
                        </HBox>

                        <HBox style={{ gap: '12px' }}>
                            <VBox style={{ gap: '4px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--color-muted)', fontWeight: 600 }}>Status</span>
                                <span style={{ fontSize: '14px', color: telemetryStatusMeta[queryOverview.data.status].color, fontWeight: 700 }}>
                                    {telemetryStatusMeta[queryOverview.data.status].label}
                                </span>
                            </VBox>
                            <VBox style={{ gap: '4px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--color-muted)', fontWeight: 600 }}>
                                    {METRIC_META[metric].label} média {period}
                                </span>
                                <span style={{ fontSize: '14px', color: 'var(--color-body)', fontWeight: 700 }}>
                                    {statValue('avg')}
                                </span>
                            </VBox>
                            <VBox style={{ gap: '4px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--color-muted)', fontWeight: 600 }}>
                                    {METRIC_META[metric].label} pico {period}
                                </span>
                                <span style={{ fontSize: '14px', color: 'var(--color-body)', fontWeight: 700 }}>
                                    {statValue('max')}
                                </span>
                            </VBox>
                            <VBox style={{ gap: '4px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--color-muted)', fontWeight: 600 }}>Última coleta</span>
                                <span style={{ fontSize: '14px', color: 'var(--color-body)', fontWeight: 700 }}>
                                    {formatCollectedAt(queryOverview.data.current?.collected_at)}
                                </span>
                            </VBox>
                        </HBox>

                        <VBox style={{ gap: '8px' }}>
                            <HBox style={{ gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '13px', color: 'var(--color-body)', fontWeight: 700 }}>
                                    {METRIC_META[metric].label} {PERIOD_META[period].chartLabel}
                                </span>
                                <HBox style={{ gap: '4px' }}>
                                    {(Object.keys(PERIOD_META) as TelemetryPeriod[]).map((p) => {
                                        const active = p === period;
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => setPeriod(p)}
                                                style={{
                                                    border: '1px solid ' + (active ? 'var(--tc-color-primary)' : 'var(--border-1)'),
                                                    background: active ? 'var(--tc-color-primary)' : 'transparent',
                                                    color: active ? '#fff' : 'var(--color-muted)',
                                                    borderRadius: '999px',
                                                    padding: '4px 12px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {PERIOD_META[p].label}
                                            </button>
                                        );
                                    })}
                                </HBox>
                            </HBox>
                            <div style={{ width: '100%', height: '320px', border: '1px solid var(--border-1)', borderRadius: '12px', padding: '12px' }}>
                                {chartData.some((point) => point.avg != null || point.max != null) ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--telemetry-chart-grid)" />
                                            <XAxis dataKey="label" minTickGap={24} tick={{ fontSize: 12 }} />
                                            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 12 }} width={42} />
                                            <Tooltip formatter={(value) => value == null ? 'n/d' : `${value}%`} />
                                            {/* Animação curta: trocar de card/período re-renderiza
                                                as linhas, e o default de 1,5s deixa a troca lenta. */}
                                            <Line type="monotone" dataKey="avg" name={`${METRIC_META[metric].label} média`} stroke="var(--telemetry-chart-avg-stroke)" strokeWidth={2} dot={false} connectNulls animationDuration={350} />
                                            <Line type="monotone" dataKey="max" name={`${METRIC_META[metric].label} pico`} stroke="var(--telemetry-chart-peak-stroke)" strokeWidth={2} dot={false} connectNulls animationDuration={350} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <HBox style={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <span style={{ color: 'var(--color-muted)' }}>Sem pontos suficientes para o gráfico ainda.</span>
                                    </HBox>
                                )}
                            </div>
                        </VBox>
                    </>
                )}
            </VBox>
        </Modal>
    );
};

export default TelemetryOverviewModal;